import { useState, useMemo, useRef, useEffect, useCallback, type JSX } from 'react'
import {
  Search, RefreshCw, Archive, Mail, MailOpen, Star, Loader2, Inbox, Plus, X, Check,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown,
  Clock, AlertTriangle, CheckCircle2, Calendar, Columns3, SlidersHorizontal,
  Megaphone, GripVertical, RotateCcw, Info, HelpCircle, Network,
} from 'lucide-react'
import { useRollingCount } from '../../hooks/useRollingCount.js'
import CreateConversationPanel, { type NewConversationData } from './CreateConversationPanel.js'
import ConversationFilterPanel, { filterGroupAnchorId, type ConversationFilterState } from './ConversationFilterPanel.js'
import SpotlightGuide, { type SpotlightStep } from './SpotlightGuide.js'
import ConversationTable, { COLUMNS, DEFAULT_VISIBLE_COLUMNS } from './ConversationTable.js'
import { api } from '../../api/client.js'
import { CHANNEL_FILTER_OPTIONS, channelMeta } from './channelMeta.js'
import { useCampaigns } from '../../hooks/useCampaigns.js'
import { useConversationStatuses } from '../../hooks/useConversationStatuses.js'
import { statusKey, statusStyles, type ConversationStatusOption } from '../../modules/conversations/statusCatalog.js'
import InboxAddChildFlow from './InboxAddChildFlow.js'
import type { AdvancedFilters } from '../../hooks/useInboxConversations.js'
import { useToast } from '../system/ToastProvider.js'
import Select from '../ui/Select.js'
import { timestampMs } from '../../utils/timezone.js'
import DatePicker from '../ui/DatePicker.js'

/** Advanced-filter keys that change the form's own UI, not the result set. */
const NON_NARROWING_FILTER_KEYS = new Set(['allAgents'])

/* ── Types ── */
export interface ConversationItem {
  id: string
  customerName: string
  /** Raw contact handle (email/phone) — preferred over the display name where
   *  the exact address matters. */
  customerHandle: string | undefined
  customerAvatar: string | undefined
  subject: string
  preview: string
  channel: 'email' | 'sms' | 'whatsapp' | 'telegram' | 'facebook' | 'instagram' | 'chat' | 'voice' | 'web' | 'youtube' | 'x'
  /** Canonical set — mirrors the conversations.status CHECK constraint. */
  status: 'open' | 'closed' | 'on_hold' | 'reopen'
  /** undefined when no priority has been set — the cell stays empty rather than guessing. */
  priority: 'low' | 'medium' | 'high' | 'urgent' | undefined
  slaStatus: 'ok' | 'nearing' | 'breached' | undefined
  isRead: boolean
  isStarred: boolean
  isArchived: boolean
  timestamp: string
  createdAt: string | undefined
  slaStartedAt: string | undefined
  assignedTo: string | undefined
  /** Currently-assigned agent id — used to disable/warn on reassign to the same agent. */
  assignedAgentId?: string | undefined
  interactionId: string | undefined
  source: string | undefined
  tags: string[]
  csatScore: number | null
  transferCount: number
  reviewedCount?: number
  approvedCount?: number
  rejectedCount?: number
  totalApprovalRequests?: number
  /* ── Linked conversations. Every relationship carries both the id (so the Linked
     column can open the other conversation) and its reference id (so the row can
     name it instead of only hinting with an icon). ── */
  isParentTicket?: boolean | undefined
  /** Parent this conversation hangs off, when it is a child. */
  parentId?: string | undefined
  parentReferenceId?: string | undefined
  /** Children hanging off this conversation. `childReferenceIds` is capped by the
   *  API (see LINKED_REF_PREVIEW); `childCount` is the true total. */
  childCount?: number | undefined
  childReferenceIds?: string[] | undefined
  /** Merge relationship — drives the Primary/Secondary inbox badge.
   *  mergedIntoReferenceId set ⇒ Secondary (folded into that primary);
   *  secondaryCount > 0 ⇒ Primary (has conversations merged into it). */
  mergedIntoId?: string | undefined
  mergedIntoReferenceId?: string | undefined
  secondaryCount?: number | undefined
  secondaryReferenceIds?: string[] | undefined
  /* ── Catalog labels resolved server-side (GET /conversations). All optional:
     a tenant that hasn't configured the catalog, or a conversation that never had
     the field set, leaves the cell empty rather than inventing a value. ── */
  typeLabel?: string | undefined
  stateLabel?: string | undefined
  /** Status as configured in `conversation_status` — label and colour for the
   *  Status cell, so the inbox shows the tenant's own wording. */
  conversationStatusId?: string | undefined
  statusLabel?: string | undefined
  statusColor?: string | undefined
  urgencyLabel?: string | undefined
  impactLabel?: string | undefined
  categoryLabel?: string | undefined
  subcategoryLabel?: string | undefined
  locationLabel?: string | undefined
  branchLabel?: string | undefined
  departmentLabel?: string | undefined
  campaignLabel?: string | undefined
  teamLabel?: string | undefined
  /** SLA clocks — when the first response / resolution is due. */
  firstResponseDueAt?: string | undefined
  resolutionDueAt?: string | undefined
  /** SLA completion markers — when the first response / resolution actually happened. */
  firstRespondedAt?: string | undefined
  resolvedAt?: string | undefined
  /** Newest conversation_activity_log entry — the system of record for "last
   *  modified", since `conversations` has no updated_at/updated_by. */
  lastModifiedAt?: string | undefined
  lastModifiedBy?: string | undefined
}

export type InboxFilter = 'all' | 'unread' | 'read' | 'starred' | 'closed' | 'nearing-sla' | 'beyond-sla' | 'archived'

/** A top-level audience tab — "Mine" vs "All conversations". Rendered as a
 *  segmented control so one inbox serves every audience. */
export interface InboxScope {
  key: string
  label: string
  icon?: JSX.Element
  count?: number | undefined
  /** Optional approved/rejected/pending breakdown for approval tabs. */
  subCounts?: { approved: number; rejected: number; pending: number }
}

/** Selection facts the bulk drawer needs but can't derive from ids alone. */
export interface InboxSelectionMeta {
  /** At least one selected conversation is a parent (has children). */
  includesParent: boolean
}

interface ConversationInboxProps {
  title: string
  subtitle?: string
  /** Count per catalog status id. Providing it replaces the header's
   *  title/subtitle with one row of clickable status chips beside the scopes. */
  statusCounts?: Record<string, number>
  /** Statuses found in the data — merged into the chips so a status that was
   *  deactivated in admin but still holds conversations is shown too. */
  statusInfo?: Array<{ id: string; name: string; color: string | null }>
  /** Show "Add Child" in the selection bar when exactly one conversation is selected. */
  canAddChild?: boolean
  /** The current page's rows. In server-filtered mode (onFilterChange given)
   *  these render as-is — the server already applied filter/search/date/page. */
  conversations: ConversationItem[]
  totalCount?: number
  loading?: boolean
  currentPage?: number
  pageSize?: number
  onPageChange?: (page: number, pageSize: number) => void
  onRefresh?: () => void
  onArchive?: (ids: string[]) => void
  onUnarchive?: (ids: string[]) => void
  onMarkRead?: (ids: string[]) => void
  onMarkUnread?: (ids: string[]) => void
  onToggleStar?: (id: string) => void
  onSelectConversation?: (id: string) => void
  onNewConversation?: () => void
  onCreateConversation?: (data: NewConversationData) => void
  defaultFilter?: InboxFilter
  scopes?: InboxScope[]
  activeScope?: string
  onScopeChange?: (scope: string) => void
  /** Providing this switches the component into server-filtered mode: filter/
   *  search/date changes call back instead of being applied client-side, and
   *  `conversations`/`totalCount`/`filterCounts` are trusted as the source of
   *  truth. Needed at scale — client-side filtering only ever saw one fetched
   *  page, which silently broke search/filters past that page. */
  onFilterChange?: (filter: InboxFilter) => void
  onSearchChange?: (query: string) => void
  onDateRangeChange?: (from: string, to: string) => void
  initialFromDate?: string
  initialToDate?: string
  onChannelChange?: (channel: string) => void
  onCampaignIdsChange?: (ids: string[]) => void
  advancedFilters?: AdvancedFilters
  onAdvancedFiltersChange?: (filters: Partial<AdvancedFilters>) => void
  onClearAdvancedFilters?: () => void
  /** Whether child tickets appear as top-level rows in the list. When false,
   *  the server excludes them (withoutParent=1); they still appear in a
   *  parent's expand tree. */
  showChildTickets?: boolean
  onShowChildTicketsChange?: (v: boolean) => void
  /** Whether secondary (merged) tickets appear as top-level rows in the list. */
  showSecondaryTickets?: boolean
  onShowSecondaryTicketsChange?: (v: boolean) => void
  /** Server-computed per-tab counts (GET /conversations/counts). Falls back to
   *  counting the current page client-side when omitted. */
  filterCounts?: Record<string, number>
  isFirstVisit?: boolean
  onDismissOnboarding?: () => void
  /** `currentAssigneeAgentIds` = distinct agents already assigned to the
   *  selected conversations, so the drawer can disable/warn against a no-op
   *  reassign to the same agent. */
  onReassign?: (conversationIds: string[], currentAssigneeAgentIds: string[]) => void
  onBulkTransfer?: (conversationIds: string[], currentAssigneeAgentIds: string[], meta: InboxSelectionMeta) => void
  onBulkArchive?: (conversationIds: string[]) => Promise<{ successCount: number; errorCount: number }>
  onBulkUnarchive?: (conversationIds: string[]) => Promise<{ successCount: number; errorCount: number }>
  /** Bump this number to clear the current selection — used by the parent after
   *  a reassign / bulk-transfer completes (the drawer lives outside this component). */
  clearSelectionSignal?: number
  canTransfer?: boolean
  canBulkTransfer?: boolean
  canArchive?: boolean
  /** Extra content rendered in the toolbar row, after the built-in filter
   *  dropdowns. Used by the Approvals page to inject the approval-state
   *  filter dropdown without forking the whole component. */
  extraToolbarContent?: JSX.Element
}

/* ── Design tokens: radius capped at 3px across the whole surface ── */
const R = 'rounded-[3px]'
/** localStorage key for the per-user column selection (order + visibility). */
const COLUMN_PREF_KEY = 'kc3_inbox_columns'
const MAX_SELECTION = 200

/**
 * Coerce an arbitrary saved column list into a valid, ordered visible-column
 * set: drop unknown keys, de-dupe, preserve the saved order, and force the
 * locked columns back in so a stale/partial preference can never render an
 * unusable table. An empty/invalid input falls back to the default set.
 */
function sanitizeColumnOrder(saved: unknown): string[] {
  if (!Array.isArray(saved)) return DEFAULT_VISIBLE_COLUMNS
  const known = new Set(COLUMNS.map((c) => c.key))
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const k of saved) {
    if (typeof k !== 'string' || !known.has(k) || seen.has(k)) continue
    seen.add(k)
    ordered.push(k)
  }
  // Locked columns are always present (their fixed positions are enforced by
  // the table itself); just guarantee they're included.
  for (const c of COLUMNS) {
    if (c.locked && !seen.has(c.key)) {
      seen.add(c.key)
      ordered.push(c.key)
    }
  }
  return ordered.length > 0 ? ordered : DEFAULT_VISIBLE_COLUMNS
}

/**
 * Coerce a server-supplied status string to the canonical set.
 *
 * The API returns raw `conversations.status` text, so callers must normalize
 * rather than cast — a blind `as` on an unmapped value (e.g. 'reopen') used to
 * crash the status cell and take the whole inbox down via the error boundary.
 */
export function normalizeConversationStatus(s: string | null | undefined): ConversationItem['status'] {
  if (s === 'closed' || s === 'resolved') return 'closed'
  if (s === 'on_hold') return 'on_hold'
  if (s === 'reopen') return 'reopen'
  return 'open'
}

const FILTERS: { key: InboxFilter; label: string; icon: JSX.Element }[] = [
  { key: 'all', label: 'All', icon: <Inbox className="h-4 w-4" /> },
  { key: 'unread', label: 'Unread', icon: <Mail className="h-4 w-4" /> },
  { key: 'read', label: 'Read', icon: <MailOpen className="h-4 w-4" /> },
  { key: 'starred', label: 'Starred', icon: <Star className="h-4 w-4" /> },
  { key: 'nearing-sla', label: 'Nearing SLA', icon: <Clock className="h-4 w-4" /> },
  { key: 'beyond-sla', label: 'Beyond SLA', icon: <AlertTriangle className="h-4 w-4" /> },
  { key: 'closed', label: 'Closed', icon: <CheckCircle2 className="h-4 w-4" /> },
  { key: 'archived', label: 'Archived', icon: <Archive className="h-4 w-4" /> },
]

/* Shared control surfaces — defined once so every affordance matches. */
const CONTROL = `flex h-8 items-center gap-2 ${R} border border-slate-200 bg-white px-2.5 text-[12.5px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900`
const CONTROL_ON = 'border-[#C9D9FB] bg-blue-50 text-[#3366FF] hover:border-[#C9D9FB] hover:text-[#3366FF]'
const ICON_BTN = `flex h-9 w-9 shrink-0 items-center justify-center ${R} border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900`
const MENU = `absolute top-full z-40 mt-1.5 overflow-hidden ${R} border border-slate-200 bg-white py-1 shadow-xl shadow-black/5`
const MENU_ITEM = 'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] font-medium transition-colors'

/**
 * Walkthrough for the advanced-filter drawer, reached from the ⓘ in its header.
 *
 * Ordered the way the form is read — narrow by identity, then who owns it, then
 * when and what kind — and finishes on the two footer actions, since "how do I
 * undo this?" is the question that follows setting a filter.
 */
const FILTER_GUIDE_STEPS: SpotlightStep[] = [
  {
    targetId: 'filter-fields-button',
    title: '1. Choose which filters you see',
    text: 'Click Fields to tick the filters you want on this form and untick the ones you never use. Removing a field also clears it, so it stops narrowing your results. Your choice is saved to your profile.',
    placement: 'bottom',
  },
  {
    targetId: filterGroupAnchorId('Details'),
    title: '2. Start with what you know',
    text: 'Have the reference? Type it into Conversation ID and nothing else is needed. Subject and Description match on part of the text and are case sensitive.',
    placement: 'bottom',
  },
  {
    targetId: filterGroupAnchorId('Organization'),
    title: '3. Narrow by who it belongs to',
    text: 'Pick a Client, then Location. Choosing a Location fills Branch; choosing a Branch fills Department. Until you pick the one before it, the next stays disabled.',
    placement: 'bottom',
  },
  {
    targetId: filterGroupAnchorId('Routing'),
    title: '4. Campaign, then Team',
    text: 'Team is empty until you choose a Campaign — pick the campaign and the team list fills with that campaign\'s teams. Changing the campaign resets Team and any agents you had selected, because those belonged to the old campaign.',
    placement: 'bottom',
  },
  {
    targetId: filterGroupAnchorId('Date Created'),
    title: '5. Bound it in time',
    text: 'Created From and Created Upto are both inclusive. Fill in just one to get everything before, or everything after, that date.',
    placement: 'bottom',
  },
  {
    targetId: filterGroupAnchorId('Classification'),
    title: '6. Narrow by what it is',
    text: 'Type, Status, Category, Priority and the rest. Category behaves like Location did: pick one and Sub Category fills with its children.',
    placement: 'bottom',
  },
  {
    targetId: 'filter-agents',
    title: '7. Pick the agents',
    text: 'The left list shows agents from the campaign and team above — click one to move it to Selected on the right, and click it there to take it back. No campaign in mind? Tick "Show all agents": the campaign and team requirement drops and every agent is listed.',
    placement: 'top',
  },
  {
    targetId: 'filter-guide-actions',
    title: '8. Apply, or start over',
    text: 'Apply closes the drawer and filters the inbox — the Filters button then shows a dot, and a Clear filters button appears with a count. Clear all filters resets everything here plus the search box and date range.',
    placement: 'top',
  },
]


/* ── Spotlight tour ──
   Ordered the way the screen reads: audience tabs, then the search and New
   button in the header, then each toolbar filter left-to-right, then the list
   itself, then paging. Every filter the toolbar offers has a step — a control
   nobody explains is a control nobody uses.

   Steps whose target is not on the page are skipped by SpotlightGuide, so this
   list stays honest for an agent without the New button, without campaigns, or
   looking at an empty inbox — no step is conditionally assembled here. */
const TOUR_STEPS: SpotlightStep[] = [
  { targetId: 'tour-scopes', title: 'My conversations vs All', text: 'Start here. "My conversations" shows only what is assigned to you; "All" shows everything you are allowed to see. The badge on each tab is its live count.', placement: 'bottom' },
  { targetId: 'tour-new-conv', title: 'New Conversation', text: 'Start a fresh conversation with any customer from here.', placement: 'bottom' },
  { targetId: 'tour-search', title: 'Smart Search', text: 'Type a name, subject, conversation number or tag to find conversations instantly.', placement: 'bottom' },
  { targetId: 'tour-filters', title: 'Quick Filters', text: 'One click to switch between Unread, Starred, Nearing SLA and Beyond SLA. The number beside each is how many conversations match.', placement: 'bottom' },
  { targetId: 'tour-channel', title: 'Channel', text: 'Narrow to a single channel — email, WhatsApp, chat, voice and the rest. Pick "All channels" to clear it.', placement: 'bottom' },
  { targetId: 'tour-campaign', title: 'Campaign', text: 'Tick one or more campaigns to see only their conversations. Several can be active at once; "All campaigns" clears the selection.', placement: 'bottom' },
  { targetId: 'tour-conv-status', title: 'Conversation status', text: 'Your own statuses, configured in admin — Open, Pending, Resolved and whatever else your team uses. The dot matches the colour set there.', placement: 'bottom' },
  { targetId: 'tour-columns', title: 'Your Columns', text: 'Show only the columns you care about, and drag to reorder them — your choice is remembered.', placement: 'bottom' },
  { targetId: 'tour-date', title: 'Date range', text: 'Limit the list to conversations created between two dates. Click Date to open the From and To boxes; the small ✕ clears both.', placement: 'bottom' },
  { targetId: 'tour-advanced', title: 'Advanced filters', text: 'Everything the toolbar does not cover — agent, team, priority, tags and more. Inside the drawer, the ⓘ icon walks you through building a filter.', placement: 'bottom' },
  { targetId: 'tour-first-row', title: 'Open a conversation', text: 'Click anywhere on a row to open the conversation in a new tab. The ★ stars it without opening, and the ⋯ menu at the end marks it read, archives it or opens it directly.', placement: 'bottom' },
  { targetId: 'tour-paging', title: 'Pagination', text: 'Move through large inboxes with page sizes, numbered pages and jump-to-page.', placement: 'top' },
  { targetId: 'tour-replay', title: 'Replay this tour', text: 'That is the whole inbox. Click here any time to run through it again.', placement: 'bottom' },
]

/** The welcome tour is the same overlay as the filter guide, different steps. */
function TourOverlay({ stepIndex, onNext, onPrev, onSkip }: {
  stepIndex: number
  onNext: () => void
  onPrev: () => void
  onSkip: () => void
}): JSX.Element | null {
  return (
    <SpotlightGuide
      steps={TOUR_STEPS}
      stepIndex={stepIndex}
      onNext={onNext}
      onPrev={onPrev}
      onSkip={onSkip}
    />
  )
}

/* ── Footer / pagination ── */
function PaginationBar({ page, pageSize, totalCount, onChange }: {
  page: number
  pageSize: number
  totalCount: number
  onChange: (page: number, pageSize: number) => void
}): JSX.Element {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, totalCount)
  const [jumpPage, setJumpPage] = useState('')
  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = []
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i) }
    else if (page <= 3) pages.push(1, 2, 3, 4, '…', totalPages)
    else if (page >= totalPages - 2) pages.push(1, '…', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
    else pages.push(1, '…', page - 1, page, page + 1, '…', totalPages)
    return pages
  }, [page, totalPages])
  const nav = `flex h-8 w-8 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:pointer-events-none disabled:opacity-30`

  return (
    <div id="tour-paging" className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 bg-white px-5 py-2.5">
      <div className="flex items-center gap-5">
        <span className="text-[12.5px] text-slate-500">
          Showing <span className="font-semibold text-slate-800">{start.toLocaleString()}–{end.toLocaleString()}</span> of <span className="font-semibold text-slate-800"><RollingCount value={totalCount} /></span>
        </span>
        <label className="flex items-center gap-2 text-[12.5px] text-slate-500">
          Rows
          <Select
            ariaLabel="Rows per page"
            size="sm"
            value={String(pageSize)}
            onChange={(v) => {
              const newSize = Number(v)
              const currentOffset = (page - 1) * pageSize
              const newPage = Math.floor(currentOffset / newSize) + 1
              onChange(newPage, newSize)
            }}
            options={[10, 20, 40, 50, 100, 200].map((s) => ({ value: String(s), label: String(s) }))}
            className="w-[70px]"
          />
        </label>
      </div>

      <div className="flex items-center gap-1">
        <button onClick={() => onChange(1, pageSize)} disabled={page <= 1} className={nav} title="First page"><ChevronsLeft className="h-4 w-4" /></button>
        <button onClick={() => onChange(Math.max(1, page - 1), pageSize)} disabled={page <= 1} className={nav} title="Previous page"><ChevronLeft className="h-4 w-4" /></button>
        <div className="flex items-center gap-1 px-1.5">
          {pageNumbers.map((p, i) => p === '…'
            ? <span key={`gap-${i}`} className="px-1 text-slate-300">…</span>
            : (
              <button
                key={p}
                onClick={() => onChange(p as number, pageSize)}
                className={`flex h-8 min-w-[2rem] items-center justify-center ${R} px-2 text-[12.5px] font-semibold transition-colors ${page === p ? 'bg-[#3366FF] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >{p}</button>
            ))}
        </div>
        <button onClick={() => onChange(Math.min(totalPages, page + 1), pageSize)} disabled={page >= totalPages} className={nav} title="Next page"><ChevronRight className="h-4 w-4" /></button>
        <button onClick={() => onChange(totalPages, pageSize)} disabled={page >= totalPages} className={nav} title="Last page"><ChevronsRight className="h-4 w-4" /></button>
        <input
          type="text" inputMode="numeric" value={jumpPage} placeholder="Go to"
          onChange={(e) => setJumpPage(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            onChange(Math.max(1, Math.min(totalPages, Number(jumpPage) || 1)), pageSize)
            setJumpPage('')
          }}
          className={`ml-2 h-8 w-16 ${R} border border-slate-200 bg-white px-2 text-center text-[12.5px] font-medium text-slate-700 outline-none placeholder:text-slate-300 focus:border-[#3366FF]`}
        />
      </div>
    </div>
  )
}

/* ── Loading / empty states ── */
const SKELETON_ROWS = 12

/** Shimmer skeleton bar — a gradient sweep instead of pulse. */
function ShimmerBar({ className, style }: { className?: string; style?: React.CSSProperties }): JSX.Element {
  return (
    <div
      className={`${className ?? ''} relative overflow-hidden bg-slate-100 ${R}`}
      style={style}
      aria-hidden="true"
    >
      <div
        className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"
      />
    </div>
  )
}

function SkeletonTable({ columns }: { columns: number }): JSX.Element {
  const shown = Math.min(columns, 9)
  return (
    <div aria-hidden="true">
      {/* Header skeleton */}
      <div className="flex h-11 items-center gap-4 border-b border-slate-200 bg-[#F7F8FA] px-3.5">
        {Array.from({ length: shown }).map((_, i) => (
          <ShimmerBar key={i} className="h-2.5" style={{ width: i === 0 ? 18 : 68 }} />
        ))}
      </div>
      {/* Row skeletons */}
      {Array.from({ length: SKELETON_ROWS }).map((_, r) => (
        <div
          key={r}
          className="flex h-[52px] items-center gap-4 border-b border-slate-100 px-3.5"
          style={{ animationDelay: `${r * 60}ms` }}
        >
          {Array.from({ length: shown }).map((_, c) => (
            <ShimmerBar
              key={c}
              className="h-3"
              style={{ width: c === 0 ? 18 : 60 + ((r + c) % 4) * 26, animationDelay: `${(r + c) * 80}ms` }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/** Animated count that rolls from old → new value. */
function RollingCount({ value, className }: { value: number; className?: string }): JSX.Element {
  const display = useRollingCount(value)
  return <span className={className}>{display.toLocaleString()}</span>
}

function EmptyState({ searchQuery, filter, loading, onCreateNew, archivedCount }: {
  searchQuery: string
  filter: string
  loading: boolean
  onCreateNew?: () => void
  archivedCount: number
}): JSX.Element {
  if (loading) {
    return (
      <div className="flex h-full flex-col items-center justify-center py-20">
        <Loader2 className="mb-3 h-6 w-6 animate-spin text-slate-300" />
        <p className="text-[13px] text-slate-400">Loading conversations…</p>
      </div>
    )
  }
  const isDefaultView = !searchQuery && filter === 'all'
  // "All" can look empty while the API has data, if every conversation is
  // archived — say so, rather than showing a message that reads like a bug.
  const allArchived = isDefaultView && archivedCount > 0
  const message = searchQuery
    ? `No conversations match "${searchQuery}". Try a different term or clear your filters.`
    : allArchived
      ? `All ${archivedCount.toLocaleString()} conversation${archivedCount === 1 ? '' : 's'} ${archivedCount === 1 ? 'is' : 'are'} archived — check the Archived tab.`
      : filter !== 'all'
        ? 'Nothing matches this filter right now.'
        : 'Your inbox is clear. New conversations will land here automatically.'
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-20">
      <div className={`mb-5 flex h-14 w-14 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}>
        <Inbox className="h-6 w-6" />
      </div>
      <h3 className="mb-1.5 text-[15px] font-bold text-[#0f172a]">No conversations</h3>
      <p className="mb-6 max-w-sm text-center text-[13px] leading-relaxed text-slate-400">{message}</p>
      {isDefaultView && !allArchived && onCreateNew && (
        <button onClick={onCreateNew} className={`flex h-9 items-center gap-2 ${R} bg-[#3366FF] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC]`}>
          <Plus className="h-4 w-4" />New conversation
        </button>
      )}
    </div>
  )
}

/** The order agents read the lifecycle in; statuses not listed follow in catalog order. */
const STATUS_CHIP_ORDER = ['open', 'in_progress', 'on_hold', 'reopen', 'closed']

function chipRank(name: string): number {
  const i = STATUS_CHIP_ORDER.indexOf(statusKey(name))
  return i === -1 ? STATUS_CHIP_ORDER.length : i
}

/** Count-per-status chips in the header — always one line. Colours come from
 *  the tenant's status catalog; clicking a chip toggles that status in the
 *  inbox filter. Tenants can define many statuses, so the row scrolls
 *  sideways (wheel, trackpad or the edge arrows) instead of wrapping. */
function StatusChips({ catalog, counts, selectedIds, onToggle }: {
  catalog: ConversationStatusOption[]
  counts: Record<string, number>
  selectedIds: string[]
  onToggle: (id: string) => void
}): JSX.Element | null {
  const ordered = catalog
    .map((st, i) => ({ st, i }))
    .sort((a, b) => chipRank(a.st.name) - chipRank(b.st.name) || a.i - b.i)
    .map(({ st }) => st)

  const scrollerRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ left: false, right: false })
  const updateEdges = useCallback((): void => {
    const el = scrollerRef.current
    if (!el) return
    // A few px of slack: at fractional zoom scrollLeft stops just short of
    // the end, which used to leave the right arrow (and fade) over the last chip.
    const max = el.scrollWidth - el.clientWidth
    const left = el.scrollLeft > 4
    const right = Math.ceil(el.scrollLeft) < max - 4
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
  }, [])
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    updateEdges()
    // Watch the chips too — the rolling counts change the row's width
    // without any scroll event.
    const ro = new ResizeObserver(updateEdges)
    ro.observe(el)
    for (const child of Array.from(el.children)) ro.observe(child)
    // A plain mouse wheel only scrolls vertically — map it onto the row.
    const onWheel = (e: WheelEvent): void => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX) || el.scrollWidth <= el.clientWidth) return
      e.preventDefault()
      el.scrollLeft += e.deltaY
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => { ro.disconnect(); el.removeEventListener('wheel', onWheel) }
  }, [updateEdges, ordered.length])
  useEffect(() => { updateEdges() }, [updateEdges, counts])
  const scrollBy = (dir: -1 | 1): void => {
    const el = scrollerRef.current
    if (el) el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.6), behavior: 'smooth' })
  }

  if (ordered.length === 0) return null
  const arrow = `absolute top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center ${R} border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-[#3366FF]/40 hover:text-[#3366FF]`
  return (
    <div className="relative min-w-0 flex-1">
      {edges.left && (
        <>
          <span aria-hidden className="pointer-events-none absolute inset-y-0 left-0 z-[5] w-10 bg-gradient-to-r from-white to-transparent" />
          <button type="button" onClick={() => scrollBy(-1)} className={`${arrow} left-0`} aria-label="Scroll statuses left">
            <ChevronLeft className="h-4 w-4" />
          </button>
        </>
      )}
      <div
        ref={scrollerRef}
        onScroll={updateEdges}
        className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
      {ordered.map((st) => {
        const on = selectedIds.includes(st.id)
        const styles = statusStyles(st.color)
        const hex = st.color && /^#[0-9a-f]{6}$/i.test(st.color.trim()) ? st.color.trim() : null
        return (
          <button
            key={st.id}
            type="button"
            onClick={() => onToggle(st.id)}
            title={on ? `Showing ${st.name} (${counts[st.id] ?? 0}) — click to clear` : `Show only ${st.name} (${counts[st.id] ?? 0})`}
            aria-pressed={on}
            className={`flex h-7 shrink-0 items-center gap-1.5 ${R} border px-2 text-[12px] font-semibold transition-all ${
              on ? 'text-[#0f172a] shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
            }`}
            style={on ? { ...styles.pill, borderColor: hex ?? '#3366FF' } : undefined}
          >
            <span className={`h-2 w-2 shrink-0 rounded-full ${st.color ? '' : 'bg-slate-400'}`} style={styles.dot} />
            <span className="max-w-[13rem] truncate">{st.name}</span>
            <span
              className={`min-w-[1.25rem] ${R} px-1 py-px text-center text-[10.5px] font-bold tabular-nums ${hex ? '' : 'bg-slate-100 text-slate-600'}`}
              style={hex ? { backgroundColor: `${hex}1F`, color: hex } : undefined}
            >
              <RollingCount value={counts[st.id] ?? 0} />
            </span>
          </button>
        )
      })}
      </div>
      {edges.right && (
        <>
          <span aria-hidden className="pointer-events-none absolute inset-y-0 right-0 z-[5] w-10 bg-gradient-to-l from-white to-transparent" />
          <button type="button" onClick={() => scrollBy(1)} className={`${arrow} right-0`} aria-label="Scroll statuses right">
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  )
}

/* ── Main component ── */
export default function ConversationInbox({
  title, subtitle, statusCounts, statusInfo, canAddChild = false, conversations, totalCount, loading = false,
  currentPage = 1, pageSize = 25, onPageChange, onRefresh,
  onArchive, onUnarchive, onMarkRead, onMarkUnread,
  onToggleStar, onSelectConversation, onNewConversation, onCreateConversation,
  defaultFilter = 'all', scopes, activeScope, onScopeChange,
  onFilterChange, onSearchChange, onDateRangeChange, initialFromDate, initialToDate, onChannelChange,
  onCampaignIdsChange, advancedFilters: advancedFiltersProp, onAdvancedFiltersChange, onClearAdvancedFilters,
  showChildTickets = true, onShowChildTicketsChange, showSecondaryTickets = true, onShowSecondaryTicketsChange,
  filterCounts: filterCountsProp, isFirstVisit = false, onDismissOnboarding,
  onReassign, onBulkTransfer, onBulkArchive, onBulkUnarchive, clearSelectionSignal,
  canTransfer = false, canBulkTransfer = false, canArchive = true, extraToolbarContent,
}: ConversationInboxProps): JSX.Element {
  // Server-filtered mode: the caller owns filtering, so `conversations` is
  // trusted as-is — no client-side re-filter/sort/paginate, which would only
  // ever see the current page.
  const serverFiltered = onFilterChange !== undefined

  const [searchQuery, setSearchQuery] = useState('')
  const [committedSearch, setCommittedSearch] = useState('')
  const [filter, setFilter] = useState<InboxFilter>(defaultFilter)
  const [channel, setChannel] = useState('')
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([])
  const [fromDate, setFromDate] = useState(initialFromDate ?? '')
  const [toDate, setToDate] = useState(initialToDate ?? '')
  const [showDateFilter, setShowDateFilter] = useState(false)
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false)
  /** Step index of the filter walkthrough; null when it isn't running. */
  const [guideStep, setGuideStep] = useState<number | null>(null)
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [channelMenuOpen, setChannelMenuOpen] = useState(false)
  const [campaignMenuOpen, setCampaignMenuOpen] = useState(false)
  const [columnMenuOpen, setColumnMenuOpen] = useState(false)
  const [ticketStatusMenuOpen, setTicketStatusMenuOpen] = useState(false)
  const [linkedMenuOpen, setLinkedMenuOpen] = useState(false)
  const statusMenuRef = useRef<HTMLDivElement>(null)
  const channelMenuRef = useRef<HTMLDivElement>(null)
  const campaignMenuRef = useRef<HTMLDivElement>(null)
  const columnMenuRef = useRef<HTMLDivElement>(null)
  const ticketStatusMenuRef = useRef<HTMLDivElement>(null)
  const linkedMenuRef = useRef<HTMLDivElement>(null)
  const [page, setPage] = useState(currentPage)
  const [size, setSize] = useState(pageSize)

  /* ── Conversation status (conversation_status catalog) ──
   * The toolbar dropdown and the advanced filter panel drive the same filter,
   * so whichever the agent uses, the other reflects it. The dropdown is
   * multi-select; the panel stays single-select and maps to/from the array. */
  const { statuses: statusCatalog } = useConversationStatuses()
  const selectedTicketStatusIds = advancedFiltersProp?.conversationStatusIds ?? []
  const selectedTicketStatuses = statusCatalog.filter((s) => selectedTicketStatusIds.includes(s.id))
  const ticketStatusLabel = selectedTicketStatuses.length === 0
    ? 'All statuses'
    : selectedTicketStatuses.length === 1
      ? (selectedTicketStatuses[0]?.name ?? 'All statuses')
      : `${selectedTicketStatuses.length} statuses`
  /* No status is pre-selected — the inbox opens on "All Status" and the agent
   * narrows it from the toolbar dropdown or the advanced filter panel. */

  // Sync local page/size when the parent's props change (e.g. hook state
  // updates after setPage is called). Without this, the PaginationBar can
  // desync from the actual data page.
  useEffect(() => { setPage(currentPage) }, [currentPage])
  useEffect(() => { setSize(pageSize) }, [pageSize])
  const [tourStep, setTourStep] = useState(0)
  const [showTour, setShowTour] = useState(isFirstVisit)
  const [createPanelOpen, setCreatePanelOpen] = useState(false)

  // Column choices are a per-user preference, not a per-visit one — an agent
  // who trimmed 24 columns to 8 shouldn't have to redo it every morning.
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(COLUMN_PREF_KEY)
      return raw ? sanitizeColumnOrder(JSON.parse(raw)) : DEFAULT_VISIBLE_COLUMNS
    } catch {
      return DEFAULT_VISIBLE_COLUMNS
    }
  })
  // Key currently being dragged in the column-reorder menu.
  const [dragColumn, setDragColumn] = useState<string | null>(null)

  // Load the saved order/visibility from the server (follows the user across
  // devices) and override the localStorage seed once it arrives.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await api.get<{ columns: string[] }>('/agent/me/inbox-columns')
        if (cancelled || !Array.isArray(res.columns) || res.columns.length === 0) return
        const next = sanitizeColumnOrder(res.columns)
        setVisibleColumns(next)
        try { localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(next)) } catch { /* preference only */ }
      } catch { /* fall back to localStorage/default */ }
    })()
    return () => { cancelled = true }
  }, [])

  // Persist a new column arrangement to both localStorage (instant) and the
  // server (durable, cross-device). Server write is fire-and-forget.
  const persistColumns = useCallback((next: string[]): void => {
    try { localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(next)) } catch { /* preference only */ }
    void api.put('/agent/me/inbox-columns', { columns: next }).catch(() => { /* best-effort */ })
  }, [])

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkArchiving, setBulkArchiving] = useState(false)

  const toast = useToast()

  const displayTotal = totalCount ?? conversations.length

  // The active tab is caller-owned (URL-driven) when scopes are in play, so a
  // scope switch that presets a filter is reflected here too.
  useEffect(() => { setFilter(defaultFilter); clearSelection() }, [defaultFilter])

  // Search is triggered explicitly (click icon or Enter), not per keystroke.
  const handleSearch = useCallback(() => {
    setCommittedSearch(searchQuery)
    clearSelection()
    setPage(1)
    if (serverFiltered) onSearchChange?.(searchQuery)
  }, [searchQuery, serverFiltered, onSearchChange])

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    setCommittedSearch('')
    clearSelection()
    setPage(1)
    if (serverFiltered) onSearchChange?.('')
  }, [serverFiltered, onSearchChange])

  // Auto-reset committed search when the input is cleared — ensures the
  // list always refreshes back to unfiltered results.
  useEffect(() => {
    if (searchQuery === '' && committedSearch !== '') {
      setCommittedSearch('')
      setPage(1)
      if (serverFiltered) onSearchChange?.('')
    }
  }, [searchQuery, committedSearch, serverFiltered, onSearchChange])

  useEffect(() => {
    if (!statusMenuOpen && !channelMenuOpen && !campaignMenuOpen && !columnMenuOpen && !ticketStatusMenuOpen) return
    const handler = (e: MouseEvent): void => {
      const t = e.target as Node
      if (statusMenuRef.current && !statusMenuRef.current.contains(t)) setStatusMenuOpen(false)
      if (channelMenuRef.current && !channelMenuRef.current.contains(t)) setChannelMenuOpen(false)
      if (campaignMenuRef.current && !campaignMenuRef.current.contains(t)) setCampaignMenuOpen(false)
      if (columnMenuRef.current && !columnMenuRef.current.contains(t)) setColumnMenuOpen(false)
      if (ticketStatusMenuRef.current && !ticketStatusMenuRef.current.contains(t)) setTicketStatusMenuOpen(false)
      if (linkedMenuRef.current && !linkedMenuRef.current.contains(t)) setLinkedMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [statusMenuOpen, channelMenuOpen, campaignMenuOpen, columnMenuOpen, ticketStatusMenuOpen, linkedMenuOpen])

  const filteredConversations = useMemo(() => {
    if (serverFiltered) return conversations
    let result = [...conversations]
    if (filter === 'archived') {
      result = result.filter((c) => c.isArchived)
    } else {
      result = result.filter((c) => !c.isArchived)
      if (filter === 'unread') result = result.filter((c) => !c.isRead)
      else if (filter === 'read') result = result.filter((c) => c.isRead)
      else if (filter === 'starred') result = result.filter((c) => c.isStarred)
      else if (filter === 'closed') result = result.filter((c) => c.status === 'closed')
      else if (filter === 'nearing-sla') result = result.filter((c) => c.slaStatus === 'nearing')
      else if (filter === 'beyond-sla') result = result.filter((c) => c.slaStatus === 'breached')
    }
    if (committedSearch.trim()) {
      const q = committedSearch.toLowerCase()
      result = result.filter((c) =>
        c.customerName.toLowerCase().includes(q)
        || c.subject.toLowerCase().includes(q)
        || c.preview.toLowerCase().includes(q)
        || (c.interactionId ?? '').toLowerCase().includes(q)
        || c.tags.some((t) => t.toLowerCase().includes(q)))
    }
    if (channel) result = result.filter((c) => c.channel === channel)
    if (fromDate) {
      const fd = new Date(fromDate); fd.setHours(0, 0, 0, 0)
      result = result.filter((c) => timestampMs(c.timestamp) >= fd.getTime())
    }
    if (toDate) {
      const td = new Date(toDate); td.setHours(23, 59, 59, 999)
      result = result.filter((c) => timestampMs(c.timestamp) <= td.getTime())
    }
    return result.sort((a, b) => timestampMs(b.timestamp) - timestampMs(a.timestamp))
  }, [conversations, filter, committedSearch, fromDate, toDate, channel, serverFiltered])

  const rows = useMemo(() => {
    if (serverFiltered) return conversations
    const start = (page - 1) * size
    return filteredConversations.slice(start, start + size)
  }, [conversations, filteredConversations, page, size, serverFiltered])

  // Identifies *which* list is on screen. Switching tab/filter/page/search
  // replaces the rows wholesale, which is not new activity — the table uses
  // this to re-baseline its "New" highlight instead of flagging the whole
  // incoming page.
  // `currentPage`/`pageSize` are in here alongside the local `page`/`size`
  // state on purpose: the props change in the same render as the new rows,
  // while the state only catches up in an effect that runs *after* the table
  // has already compared them.
  const highlightKey = useMemo(
    () => [
      activeScope ?? '', filter, committedSearch, channel, page, size, currentPage, pageSize,
      fromDate, toDate, selectedCampaignIds.join(','),
      JSON.stringify(advancedFiltersProp ?? {}),
    ].join('|'),
    [activeScope, filter, committedSearch, channel, page, size, currentPage, pageSize, fromDate, toDate, selectedCampaignIds, advancedFiltersProp],
  )

  const toggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        if (next.size >= MAX_SELECTION) {
          toast.warning('Selection limit', `You can select only ${MAX_SELECTION} conversations at a time`)
          return prev
        }
        next.add(id)
      }
      return next
    })
  }

  const toggleSelectAll = (): void => {
    setSelectedIds((prev) => {
      const pageIds = rows.map((r) => r.id)
      const allSelected = pageIds.length > 0 && pageIds.every((id) => prev.has(id))
      if (allSelected) {
        const next = new Set(prev)
        pageIds.forEach((id) => next.delete(id))
        return next
      }
      const capped = pageIds.slice(0, MAX_SELECTION)
      if (capped.length < pageIds.length) {
        toast.warning('Selection limit', `You can select only ${MAX_SELECTION} conversations at a time`)
      }
      return new Set(capped)
    })
  }

  const clearSelection = (): void => setSelectedIds(new Set())

  // Parent bumps clearSelectionSignal after a reassign / bulk-transfer succeeds
  // (that flow's drawer lives in the parent), so the selection header clears.
  useEffect(() => {
    if (clearSelectionSignal === undefined) return
    setSelectedIds(new Set())
  }, [clearSelectionSignal])

  // Distinct agent ids already assigned to the selected conversations — passed
  // to the reassign drawer so it can disable/warn against a no-op reassign.
  const selectedAssigneeAgentIds = (): string[] => {
    const ids = new Set<string>()
    for (const c of conversations) {
      if (selectedIds.has(c.id) && c.assignedAgentId) ids.add(c.assignedAgentId)
    }
    return Array.from(ids)
  }

  const handleReassignClick = (): void => {
    onReassign?.(Array.from(selectedIds), selectedAssigneeAgentIds())
  }

  const handleBulkTransferClick = (): void => {
    const includesParent = conversations.some((c) => selectedIds.has(c.id) && (c.childCount ?? 0) > 0)
    onBulkTransfer?.(Array.from(selectedIds), selectedAssigneeAgentIds(), { includesParent })
  }

  /* ── Add Child (single selection) ──
   * Same gating as the conversation view's rail: assignee only, not closed, and
   * not itself a child. A selected id that isn't a top-level row is an expanded
   * child ticket — those can't have children either. */
  const [addChildFor, setAddChildFor] = useState<{ id: string; referenceId?: string | undefined } | null>(null)
  const [myAgentId, setMyAgentId] = useState<string | null>(null)
  useEffect(() => {
    if (!canAddChild) return
    let cancelled = false
    api.get<{ id: string }>('/agent/me')
      .then((res) => { if (!cancelled) setMyAgentId(res.id) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [canAddChild])
  const singleSelectedId = selectedIds.size === 1 ? Array.from(selectedIds)[0]! : null
  const singleSelectedRow = singleSelectedId ? conversations.find((c) => c.id === singleSelectedId) : undefined
  const addChildBlockedReason = !singleSelectedId
    ? undefined
    : !singleSelectedRow || singleSelectedRow.parentId
      ? "A child conversation can't have its own children"
      // Same set the inbox's Closed filter shows.
      : singleSelectedRow.status === 'closed'
        ? 'Conversation is closed'
        : myAgentId === null || singleSelectedRow.assignedAgentId !== myAgentId
          ? 'Assign the conversation to yourself first'
          : undefined

  const handleBulkArchiveClick = async (): Promise<void> => {
    if (!onBulkArchive) return
    setBulkArchiving(true)
    try {
      const result = await onBulkArchive(Array.from(selectedIds))
      if (result.errorCount > 0) {
        toast.warning('Archive completed with errors', `${result.successCount} succeeded, ${result.errorCount} failed`)
      } else {
        toast.success('Archived', `${result.successCount} conversation(s) archived`)
      }
      clearSelection()
    } finally {
      setBulkArchiving(false)
    }
  }

  const handleBulkUnarchiveClick = async (): Promise<void> => {
    if (!onBulkUnarchive) return
    setBulkArchiving(true)
    try {
      const result = await onBulkUnarchive(Array.from(selectedIds))
      if (result.errorCount > 0) {
        toast.warning('Unarchive completed with errors', `${result.successCount} succeeded, ${result.errorCount} failed`)
      } else {
        toast.success('Unarchived', `${result.successCount} conversation(s) unarchived`)
      }
      clearSelection()
    } finally {
      setBulkArchiving(false)
    }
  }

  const handlePageChange = (newPage: number, newSize: number): void => {
    setPage(newPage); setSize(newSize)
    clearSelection()
    onPageChange?.(newPage, newSize)
  }
  const handleFilterChange = (next: InboxFilter): void => {
    setFilter(next); setPage(1)
    clearSelection()
    // The Closed tab and a status filter of "Open" cancel each other out — an
    // agent clicking Closed wants the closed conversations, not an empty table.
    if (next === 'closed' && selectedTicketStatusIds.length > 0) {
      onAdvancedFiltersChange?.({ conversationStatusIds: [] })
    }
    onFilterChange?.(next)
  }
  // Multi-select: each click toggles the status and the menu stays open so
  // the agent can keep picking. "All statuses" clears the selection.
  const handleTicketStatusToggle = (statusId: string): void => {
    setPage(1)
    clearSelection()
    const next = selectedTicketStatusIds.includes(statusId)
      ? selectedTicketStatusIds.filter((id) => id !== statusId)
      : [...selectedTicketStatusIds, statusId]
    onAdvancedFiltersChange?.({ conversationStatusIds: next })
  }
  const handleTicketStatusClear = (): void => {
    setPage(1); setTicketStatusMenuOpen(false)
    clearSelection()
    onAdvancedFiltersChange?.({ conversationStatusIds: [] })
  }
  const handleChannelChange = (next: string): void => {
    setChannel(next); setPage(1); setChannelMenuOpen(false)
    clearSelection()
    onChannelChange?.(next)
  }
  const handleCampaignToggle = (id: string): void => {
    clearSelection()
    setSelectedCampaignIds((prev) => {
      const next = prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
      onCampaignIdsChange?.(next)
      return next
    })
    setPage(1)
  }
  const handleCampaignClear = (): void => {
    clearSelection()
    setSelectedCampaignIds([]); setPage(1)
    onCampaignIdsChange?.([])
  }
  const handleDateChange = (next: { from?: string; to?: string }): void => {
    clearSelection()
    const nextFrom = next.from ?? fromDate
    const nextTo = next.to ?? toDate
    if (next.from !== undefined) setFromDate(next.from)
    if (next.to !== undefined) setToDate(next.to)
    setPage(1)
    onDateRangeChange?.(nextFrom, nextTo)
  }
  const dismissTour = (): void => { setShowTour(false); onDismissOnboarding?.() }

  const toggleColumn = (key: string): void => {
    setVisibleColumns((prev) => {
      if (COLUMNS.find((c) => c.key === key)?.locked) return prev
      // Preserve the user's custom order: enabling appends to the end, disabling
      // removes in place. (The table pins the locked sticky columns itself.)
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      persistColumns(next)
      return next
    })
  }
  // Live reorder while dragging: move `fromKey` to `toKey`'s slot in state only
  // (no persistence) so the list shuffles smoothly under the cursor. The final
  // order is committed once on drag end.
  const reorderColumnLocal = (fromKey: string, toKey: string): void => {
    if (fromKey === toKey) return
    setVisibleColumns((prev) => {
      const from = prev.indexOf(fromKey)
      const to = prev.indexOf(toKey)
      if (from < 0 || to < 0 || from === to) return prev
      const next = [...prev]
      next.splice(from, 1)
      next.splice(to, 0, fromKey)
      return next
    })
  }
  // Commit the current arrangement to localStorage + server (called on drop/end).
  const commitColumnOrder = (): void => {
    setDragColumn(null)
    setVisibleColumns((prev) => { persistColumns(prev); return prev })
  }
  const resetColumns = (): void => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS)
    persistColumns(DEFAULT_VISIBLE_COLUMNS)
  }

  /* Client-side badge counts — only meaningful in uncontrolled/demo mode; the
     server supplies real ones via filterCounts. */
  const active = useMemo(() => conversations.filter((c) => !c.isArchived), [conversations])
  const archivedCount = useMemo(() => conversations.filter((c) => c.isArchived).length, [conversations])
  const localCounts: Record<string, number> = {
    all: active.length,
    unread: active.filter((c) => !c.isRead).length,
    read: active.filter((c) => c.isRead).length,
    starred: active.filter((c) => c.isStarred).length,
    closed: active.filter((c) => c.status === 'closed').length,
    'nearing-sla': active.filter((c) => c.slaStatus === 'nearing').length,
    'beyond-sla': active.filter((c) => c.slaStatus === 'breached').length,
    archived: archivedCount,
  }
  const filterCounts = serverFiltered ? (filterCountsProp ?? {}) : localCounts

  const { campaigns } = useCampaigns()

  /* Map AdvancedFilters ↔ ConversationFilterState for the reusable panel. */
  const filterState: ConversationFilterState = useMemo(() => ({
    keyword: '', allAgents: advancedFiltersProp?.allAgents ?? false,
    referenceId: advancedFiltersProp?.referenceId ?? '',
    clientId: advancedFiltersProp?.clientId ?? '',
    subject: advancedFiltersProp?.subject ?? '', description: advancedFiltersProp?.description ?? '',
    status: advancedFiltersProp?.status ?? '',
    // The reusable panel is single-select; it maps onto the first entry of
    // the multi-select toolbar filter.
    conversationStatusId: advancedFiltersProp?.conversationStatusIds?.[0] ?? '', channel: '',
    typeId: advancedFiltersProp?.typeId ?? '', categoryId: advancedFiltersProp?.categoryId ?? '',
    subcategoryId: advancedFiltersProp?.subcategoryId ?? '', priorityId: advancedFiltersProp?.priorityId ?? '',
    impactId: advancedFiltersProp?.impactId ?? '', urgencyId: advancedFiltersProp?.urgencyId ?? '',
    complexityId: advancedFiltersProp?.complexityId ?? '', campaignId: advancedFiltersProp?.campaignId ?? '',
    teamId: advancedFiltersProp?.assignedTeamId ?? '', agentIds: advancedFiltersProp?.assignedAgentIds ?? [],
    locationId: advancedFiltersProp?.locationId ?? '', branchId: advancedFiltersProp?.branchId ?? '',
    departmentId: advancedFiltersProp?.departmentId ?? '',
    stateId: advancedFiltersProp?.stateId ?? '', approvalStateId: advancedFiltersProp?.approvalStateId ?? '',
    approvalLevelId: advancedFiltersProp?.approvalLevelId ?? '',
    dynamicProperty: advancedFiltersProp?.dynamicProperty ?? '', dynamicPropertyValue: advancedFiltersProp?.dynamicPropertyValue ?? '',
    from: fromDate, to: toDate,
  }), [advancedFiltersProp, fromDate, toDate])

  const handleFilterPanelChange = useCallback((change: Partial<ConversationFilterState>) => {
    if (change.from !== undefined || change.to !== undefined) {
      handleDateChange({ from: change.from ?? '', to: change.to ?? '' })
    }
    if (!onAdvancedFiltersChange) return
    const mapped: Partial<AdvancedFilters> = {}
    if (change.referenceId !== undefined) mapped.referenceId = change.referenceId
    if (change.clientId !== undefined) mapped.clientId = change.clientId
    if (change.status !== undefined) mapped.status = change.status
    if (change.conversationStatusId !== undefined) mapped.conversationStatusIds = change.conversationStatusId ? [change.conversationStatusId] : []
    if (change.typeId !== undefined) mapped.typeId = change.typeId
    if (change.categoryId !== undefined) mapped.categoryId = change.categoryId
    if (change.subcategoryId !== undefined) mapped.subcategoryId = change.subcategoryId
    if (change.priorityId !== undefined) mapped.priorityId = change.priorityId
    if (change.impactId !== undefined) mapped.impactId = change.impactId
    if (change.urgencyId !== undefined) mapped.urgencyId = change.urgencyId
    if (change.complexityId !== undefined) mapped.complexityId = change.complexityId
    if (change.campaignId !== undefined) mapped.campaignId = change.campaignId
    if (change.teamId !== undefined) mapped.assignedTeamId = change.teamId
    if (change.agentIds !== undefined) mapped.assignedAgentIds = change.agentIds
    // Without this the checkbox never reaches the filter state, so it reverts
    // to false on the very next render and appears to do nothing at all.
    if (change.allAgents !== undefined) mapped.allAgents = change.allAgents
    if (change.locationId !== undefined) mapped.locationId = change.locationId
    if (change.branchId !== undefined) mapped.branchId = change.branchId
    if (change.departmentId !== undefined) mapped.departmentId = change.departmentId
    if (change.subject !== undefined) mapped.subject = change.subject
    if (change.description !== undefined) mapped.description = change.description
    if (change.stateId !== undefined) mapped.stateId = change.stateId
    if (change.approvalStateId !== undefined) mapped.approvalStateId = change.approvalStateId
    if (change.approvalLevelId !== undefined) mapped.approvalLevelId = change.approvalLevelId
    if (change.dynamicProperty !== undefined) mapped.dynamicProperty = change.dynamicProperty
    if (change.dynamicPropertyValue !== undefined) mapped.dynamicPropertyValue = change.dynamicPropertyValue
    onAdvancedFiltersChange(mapped)
  }, [onAdvancedFiltersChange, handleDateChange])

  /**
   * Clears every refinement in one go — the only reset the inbox offers.
   *
   * Clearing `searchQuery` alone emptied the box but left `committedSearch`
   * (and the server-side `search` param) in place, so the list never changed
   * and the button looked broken. The committed value and the server are reset
   * here too, and the list returns to page 1 so the results aren't an empty
   * page N of a now-larger set.
   */
  const clearAll = useCallback((): void => {
    setSearchQuery('')
    setCommittedSearch('')
    if (serverFiltered) onSearchChange?.('')
    handleChannelChange('')
    handleCampaignClear()
    handleDateChange({ from: '', to: '' })
    onClearAdvancedFilters?.()
    onPageChange?.(1, size)
  }, [serverFiltered, onSearchChange, handleChannelChange, handleCampaignClear, handleDateChange, onClearAdvancedFilters, onPageChange, size])

  const handleFilterPanelReset = useCallback(() => {
    clearAll()
  }, [clearAll])

  /**
   * True only when an advanced filter genuinely narrows the list.
   *
   * The old test was `v !== ''`, which counted *any* non-empty-string value —
   * so the boolean `allAgents: false` default read as an active filter and the
   * inbox claimed one filter was on before you had touched anything. Each type
   * is now checked against its own "unset" value.
   */
  const advancedFilterActive = Boolean(
    advancedFiltersProp
    && Object.entries(advancedFiltersProp).some(([key, v]) => {
      // `allAgents` only widens which agents the picker offers — it is never
      // sent to the server and narrows nothing, so it is not a filter.
      if (NON_NARROWING_FILTER_KEYS.has(key)) return false
      if (typeof v === 'boolean') return v
      if (Array.isArray(v)) return v.length > 0
      if (typeof v === 'string') return v.trim() !== ''
      return v != null
    }),
  )
  const dateActive = Boolean(fromDate || toDate)
  const hiddenColumns = COLUMNS.length - visibleColumns.length
  const activeChannel = CHANNEL_FILTER_OPTIONS.find((c) => c.value === channel) ?? CHANNEL_FILTER_OPTIONS[0]!
  const ActiveChannelIcon = channel ? channelMeta(channel).icon : null
  const activeFilterMeta = FILTERS.find((f) => f.key === filter) ?? FILTERS[0]!
  // "Clear" appears only once something beyond the tab narrows the list; the
  // tab itself stays put so the view never jumps unexpectedly.
  // Counted rather than a bare boolean so the button can say how much it clears.
  const refinementCount =
    (committedSearch || searchQuery ? 1 : 0)
    + (channel ? 1 : 0)
    + (selectedCampaignIds.length > 0 ? 1 : 0)
    + (dateActive ? 1 : 0)
    + (advancedFilterActive ? 1 : 0)
  const anyRefinement = refinementCount > 0


  return (
    <div className="flex h-full flex-col bg-[#F4F6F8]">
      {showTour && (
        <TourOverlay
          stepIndex={tourStep}
          onNext={() => { if (tourStep >= TOUR_STEPS.length - 1) dismissTour(); else setTourStep((s) => s + 1) }}
          onPrev={() => setTourStep((s) => Math.max(0, s - 1))}
          onSkip={dismissTour}
        />
      )}

      {/* ── Header: identity · audience · search · new ── */}
      <header className={`flex items-center border-b border-slate-200 bg-white px-5 ${
        statusCounts ? 'flex-nowrap gap-3 py-2.5' : 'flex-wrap gap-x-5 gap-y-3 py-3'
      }`}>
        {!statusCounts && (
          <div className="flex shrink-0 items-center gap-3">
            <span className={`flex h-10 w-10 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}>
              <Inbox className="h-5 w-5" />
            </span>
            <div>
              <h1 className="text-[17px] font-bold leading-tight tracking-tight text-[#0f172a]">{title}</h1>
              <p className="text-[12px] leading-tight text-slate-400">{subtitle ?? <><RollingCount value={displayTotal} /> conversations</>}</p>
            </div>
          </div>
        )}

        {scopes && scopes.length > 0 && (
          <div id="tour-scopes" className={`flex shrink-0 items-center gap-1 ${R} bg-slate-100 p-1`}>
            {scopes.map((s) => {
              const on = s.key === activeScope
              return (
                <button
                  key={s.key}
                  onClick={() => onScopeChange?.(s.key)}
                  className={`flex items-center gap-2 ${R} px-3.5 py-1.5 text-[13px] font-semibold transition-all ${on ? 'bg-white text-[#0f172a] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  {s.icon && <span className={on ? 'text-[#3366FF]' : 'text-slate-400'}>{s.icon}</span>}
                  {s.label}
                  {s.count !== undefined && (
                    <span className={`${R} px-1.5 py-0.5 text-[11px] font-bold tabular-nums ${on ? 'bg-blue-50 text-[#3366FF]' : 'bg-slate-200/70 text-slate-500'}`}>
                      <RollingCount value={s.count} />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        {statusCounts && (
          <>
            <span className="h-6 w-px shrink-0 bg-slate-200" aria-hidden />
            <StatusChips
              catalog={[
                ...statusCatalog,
                ...(statusInfo ?? [])
                  .filter((st) => !statusCatalog.some((c) => c.id === st.id))
                  .map((st) => ({ ...st, colorType: null })),
              ]}
              counts={statusCounts}
              selectedIds={selectedTicketStatusIds}
              onToggle={handleTicketStatusToggle}
            />
          </>
        )}

        <div className={`ml-auto flex items-center justify-end gap-2 ${statusCounts ? 'shrink-0' : 'min-w-0 flex-1'}`}>
          <div id="tour-search" className={`relative ${statusCounts ? 'w-[9rem] xl:w-[13rem] 2xl:w-[20rem]' : 'w-full min-w-[12rem] max-w-[26rem]'}`}>
            <input
              type="text"
              placeholder="Search by customer, subject or conversation #…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch() } }}
              className={`h-9 w-full ${R} border border-slate-200 bg-[#FAFBFC] pl-3 pr-20 text-[13px] text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-[#C9D9FB] focus:bg-white focus:shadow-[0_0_0_3px_rgba(51,102,255,0.08)]`}
            />
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              {searchQuery && (
                <button onClick={handleClearSearch} className={`${R} p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`} title="Clear">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <button onClick={handleSearch} className={`${R} p-1.5 bg-blue-50 text-[#3366FF] transition-colors hover:bg-[#3366FF] hover:text-white`} title="Search">
                <Search className="h-4 w-4" />
              </button>
            </div>
          </div>
          <button onClick={onRefresh} className={ICON_BTN} title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {onNewConversation && (
            <button
              id="tour-new-conv"
              onClick={() => { onNewConversation(); setCreatePanelOpen(true) }}
              className={`flex h-9 shrink-0 items-center gap-1.5 ${R} bg-[#3366FF] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC]`}
            >
              <Plus className="h-4 w-4" />New
            </button>
          )}
        </div>
      </header>

      {/* ── Toolbar: status dropdown · refinements — all left-aligned ── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-200 bg-white px-5 py-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* Status (All/Unread/Starred/Nearing SLA/Beyond SLA/Closed/Archived) */}
          <div ref={statusMenuRef} id="tour-filters" className="relative">
            <button onClick={() => setStatusMenuOpen((v) => !v)} className={`${CONTROL} ${filter !== 'all' ? CONTROL_ON : ''}`}>
              <span className={filter !== 'all' ? 'text-[#3366FF]' : 'text-slate-400'}>{activeFilterMeta.icon}</span>
              {activeFilterMeta.label}
              {filterCounts[filter] !== undefined && filterCounts[filter]! > 0 && (
                <span className={`text-[11px] font-bold tabular-nums ${filter !== 'all' ? 'text-[#3366FF]' : 'text-slate-400'}`}><RollingCount value={filterCounts[filter]!} /></span>
              )}
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${statusMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {statusMenuOpen && (
              <div className={`${MENU} left-0 w-52`}>
                {FILTERS.map((f) => {
                  const on = filter === f.key
                  const count = filterCounts[f.key]
                  return (
                    <button
                      key={f.key}
                      onClick={() => { handleFilterChange(f.key); setStatusMenuOpen(false) }}
                      className={`${MENU_ITEM} ${on ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                      <span className={on ? 'text-[#3366FF]' : 'text-slate-400'}>{f.icon}</span>
                      <span className="flex-1">{f.label}</span>
                      {count !== undefined && count > 0 && (
                        <span className={`text-[11px] font-bold tabular-nums ${on ? 'text-[#3366FF]' : 'text-slate-400'}`}><RollingCount value={count} /></span>
                      )}
                      {on && <Check className="h-4 w-4" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Channel */}
          <div ref={channelMenuRef} id="tour-channel" className="relative">
            <button onClick={() => setChannelMenuOpen((v) => !v)} className={`${CONTROL} ${channel ? CONTROL_ON : ''}`}>
              {ActiveChannelIcon ? <ActiveChannelIcon className="h-4 w-4" /> : <Inbox className="h-4 w-4 text-slate-400" />}
              {activeChannel.label}
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${channelMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {channelMenuOpen && (
              <div className={`${MENU} left-0 w-52`}>
                {CHANNEL_FILTER_OPTIONS.map((c) => {
                  const on = channel === c.value
                  const meta = c.value ? channelMeta(c.value) : null
                  const Icon = meta?.icon
                  return (
                    <button key={c.value || 'all'} onClick={() => handleChannelChange(c.value)} className={`${MENU_ITEM} ${on ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}>
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center ${R} ${meta ? `${meta.bg} ${meta.fg}` : 'bg-slate-100 text-slate-500'}`}>
                        {Icon ? <Icon className="h-3.5 w-3.5" /> : <Inbox className="h-3.5 w-3.5" />}
                      </span>
                      <span className="flex-1 truncate">{c.label}</span>
                      {on && <Check className="h-4 w-4" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Campaign */}
          <div ref={campaignMenuRef} id="tour-campaign" className="relative">
            <button onClick={() => setCampaignMenuOpen((v) => !v)} className={`${CONTROL} ${selectedCampaignIds.length > 0 ? CONTROL_ON : ''}`}>
              <Megaphone className={`h-4 w-4 ${selectedCampaignIds.length > 0 ? 'text-[#3366FF]' : 'text-slate-400'}`} />
              {selectedCampaignIds.length === 0 ? 'All campaigns' : `${selectedCampaignIds.length} campaign${selectedCampaignIds.length > 1 ? 's' : ''}`}
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${campaignMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {campaignMenuOpen && (
              <div className={`${MENU} right-0 w-60`}>
                <button onClick={handleCampaignClear} className={`${MENU_ITEM} ${selectedCampaignIds.length === 0 ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <Megaphone className="h-4 w-4 shrink-0" />
                  <span className="flex-1">All campaigns</span>
                  {selectedCampaignIds.length === 0 && <Check className="h-4 w-4" />}
                </button>
                {campaigns.length > 0 && <div className="my-1 border-t border-slate-100" />}
                <div className="max-h-64 overflow-y-auto">
                  {campaigns.map((c) => {
                    const on = selectedCampaignIds.includes(c.id)
                    return (
                      <button key={c.id} onClick={() => handleCampaignToggle(c.id)} className={`${MENU_ITEM} ${on ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}>
                        <span className={`flex h-4 w-4 shrink-0 items-center justify-center ${R} border ${on ? 'border-[#3366FF] bg-[#3366FF] text-white' : 'border-slate-300'}`}>
                          {on && <Check className="h-3 w-3" strokeWidth={3} />}
                        </span>
                        <span className="flex-1 truncate">{c.name}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Conversation status — the tenant's own statuses, straight from
              conversation_status, with the colour configured in admin. */}
          <div ref={ticketStatusMenuRef} id="tour-conv-status" className="relative">
            <button
              onClick={() => setTicketStatusMenuOpen((v) => !v)}
              className={`${CONTROL} ${selectedTicketStatusIds.length > 0 ? CONTROL_ON : ''}`}
              title="Filter by conversation status"
            >
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${selectedTicketStatuses[0]?.color ? '' : 'bg-slate-400'}`}
                style={statusStyles(selectedTicketStatuses[0]?.color ?? null).dot}
              />
              {ticketStatusLabel}
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${ticketStatusMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {ticketStatusMenuOpen && (
              <div className={`${MENU} left-0 w-52`}>
                <button
                  onClick={handleTicketStatusClear}
                  className={`${MENU_ITEM} ${selectedTicketStatusIds.length === 0 ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  <span className="h-2 w-2 shrink-0 rounded-full bg-slate-400" />
                  <span className="flex-1 truncate">All statuses</span>
                  {selectedTicketStatusIds.length === 0 && <Check className="h-4 w-4" />}
                </button>
                {statusCatalog.length > 0 && <div className="my-1 border-t border-slate-100" />}
                <div className="max-h-64 overflow-y-auto">
                  {statusCatalog.map((st) => {
                    const on = selectedTicketStatusIds.includes(st.id)
                    return (
                      <button
                        key={st.id}
                        onClick={() => handleTicketStatusToggle(st.id)}
                        className={`${MENU_ITEM} ${on ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}
                      >
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${st.color ? '' : 'bg-slate-400'}`}
                          style={statusStyles(st.color).dot}
                        />
                        <span className="flex-1 truncate">{st.name}</span>
                        {on && <Check className="h-4 w-4" />}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Columns */}
          <div ref={columnMenuRef} className="relative" id="tour-columns">
            <button onClick={() => setColumnMenuOpen((v) => !v)} className={`${CONTROL} ${hiddenColumns > 0 ? CONTROL_ON : ''}`} title="Choose columns">
              <Columns3 className={`h-4 w-4 ${hiddenColumns > 0 ? 'text-[#3366FF]' : 'text-slate-400'}`} />
              Columns
              {hiddenColumns > 0 && <span className="text-[11px] font-bold tabular-nums">{visibleColumns.length}/{COLUMNS.length}</span>}
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${columnMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {columnMenuOpen && (() => {
              const visibleReorderable = visibleColumns
                .map((k) => COLUMNS.find((c) => c.key === k))
                .filter((c): c is typeof COLUMNS[number] => c !== undefined && !c.locked)
              const hiddenColumnsList = COLUMNS.filter((c) => !c.locked && !visibleColumns.includes(c.key))
              return (
                <div className={`${MENU} right-0 w-64`}>
                  <div className="flex items-center justify-between px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Visible columns
                    <button onClick={resetColumns} className="text-[11px] font-semibold normal-case tracking-normal text-[#3366FF] hover:underline">Reset</button>
                  </div>
                  <div className="max-h-80 overflow-y-auto border-t border-slate-100 px-1.5 py-1.5" onDragOver={(e) => e.preventDefault()}>
                    <div className="px-1.5 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-300">Drag to reorder</div>
                    {visibleReorderable.length === 0 && (
                      <div className="px-1.5 py-2 text-[12px] text-slate-400">No optional columns shown</div>
                    )}
                    {visibleReorderable.map((col) => {
                      const dragging = dragColumn === col.key
                      return (
                        <div
                          key={col.key}
                          draggable
                          onDragStart={(e) => { setDragColumn(col.key); e.dataTransfer.effectAllowed = 'move' }}
                          onDragEnter={() => { if (dragColumn && dragColumn !== col.key) reorderColumnLocal(dragColumn, col.key) }}
                          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }}
                          onDrop={(e) => { e.preventDefault(); commitColumnOrder() }}
                          onDragEnd={commitColumnOrder}
                          className={`group mb-0.5 flex select-none items-center gap-2 ${R} border px-2 py-2 text-[13px] transition-[background-color,box-shadow,opacity,transform] duration-200 ease-out ${
                            dragging
                              ? 'scale-[1.02] border-[#3366FF]/40 bg-blue-50/80 text-slate-800 opacity-90 shadow-[0_6px_16px_-6px_rgba(51,102,255,0.45)]'
                              : 'border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <span className={`cursor-grab text-slate-300 transition-colors active:cursor-grabbing ${dragging ? 'text-[#3366FF]' : 'group-hover:text-slate-500'}`} title="Drag to reorder">
                            <GripVertical className="h-4 w-4" />
                          </span>
                          <button onClick={() => toggleColumn(col.key)} className={`flex h-4 w-4 shrink-0 items-center justify-center ${R} border border-[#3366FF] bg-[#3366FF] text-white transition-transform hover:scale-110`} title="Hide column">
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </button>
                          <span className="flex-1 truncate">{col.label}</span>
                        </div>
                      )
                    })}
                    {hiddenColumnsList.length > 0 && (
                      <>
                        <div className="mt-1 border-t border-slate-100 px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-300">Hidden columns</div>
                        {hiddenColumnsList.map((col) => (
                          <button key={col.key} onClick={() => toggleColumn(col.key)} className={`${MENU_ITEM} text-slate-400 hover:bg-slate-50`}>
                            <span className={`ml-6 flex h-4 w-4 shrink-0 items-center justify-center ${R} border border-slate-300`} />
                            <span className="flex-1 truncate">{col.label}</span>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )
            })()}
          </div>

          {/* Show / hide linked tickets (child & secondary) in the main list.
              Child tickets still appear in a parent's expand tree regardless. */}
          <div ref={linkedMenuRef} className="relative">
            <button
              onClick={() => setLinkedMenuOpen((v) => !v)}
              className={`${CONTROL} ${(!showChildTickets || !showSecondaryTickets) ? CONTROL_ON : ''}`}
              title="Show or hide linked tickets"
            >
              <Network className={`h-4 w-4 ${(!showChildTickets || !showSecondaryTickets) ? 'text-[#3366FF]' : 'text-slate-400'}`} />
              Linked
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${linkedMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {linkedMenuOpen && (
              <div className={`${MENU} right-0 w-56`}>
                <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Show in list</div>
                <button
                  onClick={() => { onShowChildTicketsChange?.(!showChildTickets); setPage(1); clearSelection() }}
                  className={`${MENU_ITEM} ${showChildTickets ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center ${R} border ${showChildTickets ? 'border-[#3366FF] bg-[#3366FF] text-white' : 'border-slate-300'}`}>
                    {showChildTickets && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className="flex-1">Child tickets</span>
                </button>
                <button
                  onClick={() => { onShowSecondaryTicketsChange?.(!showSecondaryTickets); setPage(1); clearSelection() }}
                  className={`${MENU_ITEM} ${showSecondaryTickets ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center ${R} border ${showSecondaryTickets ? 'border-[#3366FF] bg-[#3366FF] text-white' : 'border-slate-300'}`}>
                    {showSecondaryTickets && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className="flex-1">Secondary tickets</span>
                </button>
              </div>
            )}
          </div>

          {extraToolbarContent}

        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          

          {/* Date range */}
          {showDateFilter || dateActive ? (
            <div id="tour-date" className="flex items-center gap-1.5">
              <DatePicker
                ariaLabel="From date"
                size="sm"
                label="From"
                value={fromDate}
                onChange={(v) => handleDateChange({ from: v })}
              />
              <DatePicker
                ariaLabel="To date"
                size="sm"
                label="To"
                value={toDate}
                onChange={(v) => handleDateChange({ to: v })}
              />
              <button onClick={() => { handleDateChange({ from: '', to: '' }); setShowDateFilter(false) }} className={`${R} p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700`} title="Clear dates">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button id="tour-date" onClick={() => setShowDateFilter(true)} className={CONTROL} title="Filter by date">
              <Calendar className="h-4 w-4 text-slate-400" />Date
            </button>
          )}

          {/* Advanced */}
          <button id="tour-advanced" onClick={() => setShowAdvancedFilter(true)} className={`${CONTROL} ${advancedFilterActive ? CONTROL_ON : ''}`} title="Advanced filters">
            <SlidersHorizontal className={`h-4 w-4 ${advancedFilterActive ? 'text-[#3366FF]' : 'text-slate-400'}`} />
            Filters
            {advancedFilterActive && <span className="h-1.5 w-1.5 rounded-full bg-[#3366FF]" />}
          </button>
          {/* Replay the tour. Dismissing it writes a localStorage flag, so
              without this the only way back was clearing site data by hand —
              undiscoverable, and it made the tour unreviewable after the first
              visit. */}
          <button
            id="tour-replay"
            onClick={() => { setTourStep(0); setShowTour(true) }}
            className={CONTROL}
            title="Replay the inbox tour"
            aria-label="Replay the inbox tour"
          >
            <HelpCircle className="h-4 w-4 text-slate-400" />
          </button>

          {anyRefinement && (
            <button
              onClick={clearAll}
              title={`Clear ${refinementCount} active filter${refinementCount === 1 ? '' : 's'}`}
              className={`flex h-8 items-center gap-1.5 ${R} border border-[#3366FF]/30 bg-blue-50 px-3 text-[12.5px] font-semibold text-[#3366FF] transition-colors hover:bg-[#E0EAFF]`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Clear filters
              <span className="rounded-full bg-[#3366FF] px-1.5 text-[10px] font-bold tabular-nums text-white">
                {refinementCount}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* ── Advanced filter drawer ── */}
      {showAdvancedFilter && (
        <>
          <div className="fixed inset-0 z-30 bg-[#0f172a]/25" onClick={() => setShowAdvancedFilter(false)} />
          <div className="fixed right-0 top-0 z-40 flex h-full w-full max-w-[1180px] flex-col bg-[#F8FAFC] shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
              <div className="flex items-center gap-3">
                <span className={`flex h-9 w-9 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}>
                  <SlidersHorizontal className="h-4 w-4" />
                </span>
                <div className="leading-tight">
                  <h2 className="text-[15px] font-bold text-[#0f172a]">Advanced filters</h2>
                  <p className="text-[12px] text-slate-400">{advancedFilterActive ? 'Filters are active' : 'Narrow the inbox by any conversation attribute'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setGuideStep(0)}
                  title="How to set filters"
                  aria-label="How to set filters"
                  className={`flex h-9 w-9 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-blue-50 hover:text-[#3366FF]`}
                >
                  <Info className="h-4 w-4" />
                </button>
                <button onClick={() => setShowAdvancedFilter(false)} className={`flex h-9 w-9 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <ConversationFilterPanel
                state={filterState}
                onChange={handleFilterPanelChange}
                onReset={handleFilterPanelReset}
                showKeyword={false}
                showChannel={false}
                showActions={false}
              />
            </div>
            {guideStep !== null && (
              <SpotlightGuide
                steps={FILTER_GUIDE_STEPS}
                stepIndex={guideStep}
                skipLabel="Close guide"
                zIndexClass="z-[120]"
                onNext={() => setGuideStep((i) => (i === null || i >= FILTER_GUIDE_STEPS.length - 1 ? null : i + 1))}
                onPrev={() => setGuideStep((i) => (i === null ? null : Math.max(0, i - 1)))}
                onSkip={() => setGuideStep(null)}
              />
            )}
            <div id="filter-guide-actions" className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-3">
              <button onClick={handleFilterPanelReset} className={`${R} px-3 py-2 text-[13px] font-semibold text-slate-500 transition-colors hover:text-slate-900`}>Clear all filters</button>
              <button onClick={() => setShowAdvancedFilter(false)} className={`${R} bg-[#3366FF] px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC]`}>Apply</button>
            </div>
          </div>
        </>
      )}

      {/* ── Bulk action toolbar ── */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 border-b border-slate-200 bg-blue-50 px-5 py-2.5">
          <span className="text-[13px] font-semibold text-[#0f172a]">
            {selectedIds.size} conversation{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="ml-auto flex items-center gap-2">
            {singleSelectedId && canAddChild && (
              <button
                onClick={() => setAddChildFor({ id: singleSelectedId, referenceId: singleSelectedRow?.interactionId })}
                disabled={bulkArchiving || addChildBlockedReason !== undefined}
                title={addChildBlockedReason ?? 'Add a child conversation under this one'}
                className={`flex h-8 items-center gap-1.5 ${R} border border-[#3366FF]/40 bg-white px-3.5 text-[12.5px] font-semibold text-[#3366FF] transition-colors hover:bg-[#F5F8FF] disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400`}
              >
                <Network className="h-3.5 w-3.5" />
                Add Child
              </button>
            )}
            {selectedIds.size === 1 && canTransfer && (
              <button
                onClick={handleReassignClick}
                disabled={bulkArchiving}
                className={`flex h-8 items-center gap-1.5 ${R} bg-[#3366FF] px-3.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2952CC] disabled:cursor-not-allowed disabled:opacity-50`}
              >
                Reassign
              </button>
            )}
            {selectedIds.size >= 2 && (canTransfer || canBulkTransfer) && (
              <button
                onClick={handleBulkTransferClick}
                disabled={bulkArchiving}
                className={`flex h-8 items-center gap-1.5 ${R} bg-[#3366FF] px-3.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2952CC] disabled:cursor-not-allowed disabled:opacity-50`}
              >
                Bulk Action
              </button>
            )}
            {/* Archived view → Bulk Unarchive; every other view → Bulk Archive. */}
            {selectedIds.size >= 2 && canArchive && filter === 'archived' && (
              <button
                onClick={() => void handleBulkUnarchiveClick()}
                disabled={bulkArchiving}
                className={`flex h-8 items-center gap-1.5 ${R} border border-slate-300 bg-white px-3.5 text-[12.5px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {bulkArchiving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Bulk Unarchive
              </button>
            )}
            {selectedIds.size >= 2 && canArchive && filter !== 'archived' && (
              <button
                onClick={() => void handleBulkArchiveClick()}
                disabled={bulkArchiving}
                className={`flex h-8 items-center gap-1.5 ${R} border border-slate-300 bg-white px-3.5 text-[12.5px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {bulkArchiving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Bulk Archive
              </button>
            )}
            <button
              onClick={clearSelection}
              className="text-[12.5px] font-semibold text-slate-500 underline-offset-2 transition-colors hover:text-slate-800 hover:underline"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* ── Top loading bar — visible on refetch / page change ── */}
      {loading && conversations.length > 0 && (
        <div className="relative h-0.5 w-full overflow-hidden bg-transparent">
          <div className="absolute inset-0 animate-[shimmer_1s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-[#3366FF] to-transparent" />
        </div>
      )}

      {/* ── Table ── */}
      <div className="scrollbar-thin relative flex-1 overflow-auto bg-white">
        {/* The refetch overlay is a *sibling* of the table, not a wrapper around
            a second copy of it. Rendering the table from two branches remounted
            it on every refetch, which reset the "New" highlight's baseline — so
            a reply arriving over the socket never lit its row up. */}
        {loading && conversations.length === 0 ? (
          <SkeletonTable columns={visibleColumns.length} />
        ) : rows.length === 0 && !loading ? (
          <EmptyState
            searchQuery={searchQuery}
            filter={filter}
            loading={loading}
            onCreateNew={() => { onNewConversation?.(); setCreatePanelOpen(true) }}
            archivedCount={archivedCount}
          />
        ) : (
          <>
            {loading && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                <Loader2 className="h-6 w-6 animate-spin text-[#3366FF]" />
              </div>
            )}
            <ConversationTable
              rows={rows}
              visibleColumns={visibleColumns}
              // No bulk-action / reassignment permission → no row-select checkbox.
              selectable={canTransfer || canBulkTransfer || canArchive}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              onOpen={(id) => onSelectConversation?.(id)}
              onToggleStar={(id) => onToggleStar?.(id)}
              onMarkRead={(ids) => onMarkRead?.(ids)}
              onMarkUnread={(ids) => onMarkUnread?.(ids)}
              onArchive={(ids) => onArchive?.(ids)}
              onUnarchive={(ids) => onUnarchive?.(ids)}
              highlightKey={highlightKey}
            />
          </>
        )}
      </div>

      {/* ── Footer ── */}
      {(serverFiltered ? displayTotal : filteredConversations.length) > 0 && (
        <PaginationBar
          page={page}
          pageSize={size}
          totalCount={serverFiltered ? displayTotal : filteredConversations.length}
          onChange={handlePageChange}
        />
      )}

      <InboxAddChildFlow
        conversationId={addChildFor?.id ?? null}
        referenceId={addChildFor?.referenceId}
        onClose={() => setAddChildFor(null)}
        onDone={() => { clearSelection(); onRefresh?.() }}
      />

      <CreateConversationPanel
        open={createPanelOpen}
        onClose={() => setCreatePanelOpen(false)}
        onCreate={(data) => { onCreateConversation?.(data); setCreatePanelOpen(false) }}
      />
    </div>
  )
}
